/**
 * Pulse Report Template Gallery
 * A beautiful modal gallery to select pre-built pulse report templates
 * Based on design patterns from SequenceTemplateGallery
 */

import React, { useState, useMemo } from "react";
import {
  Modal as AntModal,
  Row,
  Col,
  Card,
  Typography,
  Space,
  Tag,
  Input,
  Button,
  Empty,
  Tooltip,
  Divider,
} from "antd";
import {
  SearchOutlined,
  DollarOutlined,
  BarChartOutlined,
  LineChartOutlined,
  DashboardOutlined,
  TeamOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  WarningOutlined,
  SyncOutlined,
  RocketOutlined,
  AlertOutlined,
  CalendarOutlined,
  SmileOutlined,
  UserOutlined,
  PieChartOutlined,
  HeartOutlined,
  RiseOutlined,
  GiftOutlined,
  AppstoreOutlined,
  GlobalOutlined,
  ExclamationCircleOutlined,
  RobotOutlined,
  InteractionOutlined,
  FundOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  ApiOutlined,
  MessageOutlined,
  UserAddOutlined,
  FlagOutlined,
  MailOutlined,
} from "@ant-design/icons";
import {
  ALL_PULSE_REPORT_TEMPLATES,
  PULSE_TEMPLATE_CATEGORIES,
  PulseReportTemplate,
} from "../pulseReportTemplates";

// Cast Modal to any to avoid TypeScript children prop issues with Ant Design
const Modal = AntModal as any;

const { Title, Text, Paragraph } = Typography;

// Icon mapping for dynamic icon rendering
const ICON_MAP: Record<string, React.ComponentType<any>> = {
  DollarOutlined,
  BarChartOutlined,
  LineChartOutlined,
  DashboardOutlined,
  TeamOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  WarningOutlined,
  SyncOutlined,
  RocketOutlined,
  AlertOutlined,
  CalendarOutlined,
  SmileOutlined,
  UserOutlined,
  PieChartOutlined,
  HeartOutlined,
  RiseOutlined,
  GiftOutlined,
  AppstoreOutlined,
  GlobalOutlined,
  ExclamationCircleOutlined,
  RobotOutlined,
  InteractionOutlined,
  FundOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  ApiOutlined,
  MessageOutlined,
  UserAddOutlined,
  FlagOutlined,
  MailOutlined,
};

// Difficulty badge colors
const DIFFICULTY_COLORS = {
  beginner: { bg: "#dcfce7", text: "#15803d", border: "#86efac" },
  intermediate: { bg: "#fef3c7", text: "#b45309", border: "#fcd34d" },
  advanced: { bg: "#fee2e2", text: "#b91c1c", border: "#fca5a5" },
};

// Report type colors
const REPORT_TYPE_COLORS: Record<string, string> = {
  invoice: "#10b981",
  usage: "#0ea5e9",
  performance: "#f59e0b",
  analytics: "#8b5cf6",
  digest: "#6366f1",
  newsletter: "#ec4899",
  product_update: "#14b8a6",
  custom: "#f97316",
};

// Schedule type labels
const SCHEDULE_LABELS: Record<string, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  on_event: "Event-triggered",
};

interface PulseTemplateGalleryProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (template: PulseReportTemplate) => void;
}

const PulseTemplateGallery: React.FC<PulseTemplateGalleryProps> = ({
  visible,
  onClose,
  onSelect,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTemplate, setSelectedTemplate] =
    useState<PulseReportTemplate | null>(null);

  // Filter templates based on category and search
  const filteredTemplates = useMemo(() => {
    return ALL_PULSE_REPORT_TEMPLATES.filter((template) => {
      const matchesCategory =
        selectedCategory === "all" ||
        template.reportType === selectedCategory ||
        (selectedCategory === "milestone" &&
          ["analytics"].includes(template.reportType) &&
          template.tags.includes("milestone"));

      const matchesSearch =
        searchQuery === "" ||
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.tags.some((tag) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        );

      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  // Render icon component dynamically
  const renderIcon = (iconName: string, style: React.CSSProperties = {}) => {
    const IconComponent = ICON_MAP[iconName];
    return IconComponent ? (
      <IconComponent style={style} />
    ) : (
      <FileTextOutlined style={style} />
    );
  };

  // Handle template selection
  const handleTemplateClick = (template: PulseReportTemplate) => {
    setSelectedTemplate(template);
  };

  // Handle use template
  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onSelect(selectedTemplate);
      setSelectedTemplate(null);
      setSelectedCategory("all");
      setSearchQuery("");
      onClose();
    }
  };

  // Template Card Component
  const TemplateCard = ({ template }: { template: PulseReportTemplate }) => {
    const isSelected = selectedTemplate?.id === template.id;
    const difficultyStyle = DIFFICULTY_COLORS[template.difficulty];
    const typeColor = REPORT_TYPE_COLORS[template.reportType] || "#6b7280";

    return (
      <Card
        hoverable
        onClick={() => handleTemplateClick(template)}
        style={{
          height: "100%",
          border: isSelected ? `2px solid ${typeColor}` : "1px solid #e5e7eb",
          borderRadius: 12,
          transition: "all 0.2s ease",
          boxShadow: isSelected
            ? `0 4px 12px ${typeColor}33`
            : "0 1px 3px rgba(0,0,0,0.08)",
          background: isSelected ? `${typeColor}08` : "#fff",
          position: "relative",
        }}
        bodyStyle={{ padding: 16 }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 10,
              background: `linear-gradient(135deg, ${typeColor}20, ${typeColor}40)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {renderIcon(template.icon, { fontSize: 22, color: typeColor })}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text
              strong
              style={{
                fontSize: 14,
                display: "block",
                lineHeight: 1.3,
                marginBottom: 4,
              }}
            >
              {template.name}
            </Text>
            <Space size={4} wrap>
              <Tag
                style={{
                  borderRadius: 4,
                  border: `1px solid ${typeColor}40`,
                  background: `${typeColor}15`,
                  color: typeColor,
                  fontSize: 10,
                  padding: "0 5px",
                  margin: 0,
                }}
              >
                {template.reportType}
              </Tag>
              <Tag
                style={{
                  borderRadius: 4,
                  border: `1px solid ${difficultyStyle.border}`,
                  background: difficultyStyle.bg,
                  color: difficultyStyle.text,
                  fontSize: 10,
                  padding: "0 5px",
                  margin: 0,
                }}
              >
                {template.difficulty}
              </Tag>
            </Space>
          </div>
        </div>

        {/* Description */}
        <Paragraph
          ellipsis={{ rows: 2 }}
          style={{
            fontSize: 12,
            color: "#6b7280",
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          {template.description}
        </Paragraph>

        {/* Stats */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 0",
            borderTop: "1px solid #f3f4f6",
          }}
        >
          <Space size={12}>
            <Tooltip title="Schedule type">
              <Space size={4}>
                <CalendarOutlined style={{ color: "#9ca3af", fontSize: 12 }} />
                <Text style={{ fontSize: 11, color: "#6b7280" }}>
                  {SCHEDULE_LABELS[template.defaultSchedule.type]}
                </Text>
              </Space>
            </Tooltip>
            <Tooltip title="Setup time">
              <Space size={4}>
                <ClockCircleOutlined
                  style={{ color: "#9ca3af", fontSize: 12 }}
                />
                <Text style={{ fontSize: 11, color: "#6b7280" }}>
                  {template.setupTime}
                </Text>
              </Space>
            </Tooltip>
          </Space>
        </div>

        {/* Selection indicator */}
        {isSelected && (
          <div
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: typeColor,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleOutlined style={{ color: "#fff", fontSize: 12 }} />
          </div>
        )}
      </Card>
    );
  };

  // Template Detail Panel
  const TemplateDetailPanel = ({
    template,
  }: {
    template: PulseReportTemplate | null;
  }) => {
    if (!template) {
      return (
        <div
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 32,
            textAlign: "center",
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <Text
                  style={{ color: "#9ca3af", display: "block", marginBottom: 8 }}
                >
                  Select a template to view details
                </Text>
                <Text style={{ fontSize: 12, color: "#d1d5db" }}>
                  Choose from {ALL_PULSE_REPORT_TEMPLATES.length} pre-built
                  pulse report templates
                </Text>
              </div>
            }
          />
        </div>
      );
    }

    const typeColor = REPORT_TYPE_COLORS[template.reportType] || "#6b7280";

    return (
      <div style={{ padding: 20, height: "100%", overflow: "auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 10,
            }}
          >
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 12,
                background: `linear-gradient(135deg, ${typeColor}20, ${typeColor}40)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {renderIcon(template.icon, { fontSize: 26, color: typeColor })}
            </div>
            <div>
              <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
                {template.name}
              </Title>
              <Space size={4}>
                <Tag color={typeColor}>{template.reportType}</Tag>
                <Tag>{template.difficulty}</Tag>
              </Space>
            </div>
          </div>
          <Paragraph style={{ color: "#6b7280", marginBottom: 0, fontSize: 13 }}>
            {template.description}
          </Paragraph>
        </div>

        {/* Purpose */}
        <div style={{ marginBottom: 16 }}>
          <Text
            strong
            style={{
              display: "block",
              marginBottom: 6,
              color: "#374151",
              fontSize: 12,
            }}
          >
            Purpose
          </Text>
          <div
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              padding: 10,
            }}
          >
            <Text style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.5 }}>
              {template.purpose}
            </Text>
          </div>
        </div>

        {/* Expected Outcomes */}
        <div style={{ marginBottom: 16 }}>
          <Text
            strong
            style={{
              display: "block",
              marginBottom: 6,
              color: "#374151",
              fontSize: 12,
            }}
          >
            Expected Outcomes
          </Text>
          <Space direction="vertical" size={3} style={{ width: "100%" }}>
            {template.expectedOutcomes.slice(0, 4).map((outcome, index) => (
              <div
                key={index}
                style={{ display: "flex", alignItems: "flex-start", gap: 6 }}
              >
                <CheckCircleOutlined
                  style={{ color: "#10b981", marginTop: 2, fontSize: 12 }}
                />
                <Text style={{ fontSize: 11, color: "#4b5563" }}>{outcome}</Text>
              </div>
            ))}
          </Space>
        </div>

        {/* Best Practices */}
        <div style={{ marginBottom: 16 }}>
          <Text
            strong
            style={{
              display: "block",
              marginBottom: 6,
              color: "#374151",
              fontSize: 12,
            }}
          >
            Best Practices
          </Text>
          <Space direction="vertical" size={3} style={{ width: "100%" }}>
            {template.bestPractices.slice(0, 4).map((practice, index) => (
              <div
                key={index}
                style={{ display: "flex", alignItems: "flex-start", gap: 6 }}
              >
                <TrophyOutlined
                  style={{ color: "#f59e0b", marginTop: 2, fontSize: 12 }}
                />
                <Text style={{ fontSize: 11, color: "#4b5563" }}>
                  {practice}
                </Text>
              </div>
            ))}
          </Space>
        </div>

        {/* Key Metrics */}
        <div style={{ marginBottom: 16 }}>
          <Text
            strong
            style={{
              display: "block",
              marginBottom: 6,
              color: "#374151",
              fontSize: 12,
            }}
          >
            Key Metrics Included
          </Text>
          <Space wrap size={4}>
            {template.keyMetrics.map((metric) => (
              <Tag
                key={metric}
                style={{
                  borderRadius: 4,
                  background: "#f0f9ff",
                  border: "1px solid #bae6fd",
                  color: "#0369a1",
                  fontSize: 10,
                }}
              >
                {metric}
              </Tag>
            ))}
          </Space>
        </div>

        {/* Default Schedule */}
        <div style={{ marginBottom: 16 }}>
          <Text
            strong
            style={{
              display: "block",
              marginBottom: 6,
              color: "#374151",
              fontSize: 12,
            }}
          >
            Suggested Schedule
          </Text>
          <div
            style={{
              background: `${typeColor}10`,
              border: `1px solid ${typeColor}30`,
              borderRadius: 8,
              padding: 10,
            }}
          >
            <Space>
              <CalendarOutlined style={{ color: typeColor }} />
              <Text style={{ fontSize: 12, color: "#4b5563" }}>
                {SCHEDULE_LABELS[template.defaultSchedule.type]}
                {template.defaultSchedule.time &&
                  ` at ${template.defaultSchedule.time}`}
                {template.defaultSchedule.eventType &&
                  ` (${template.defaultSchedule.eventType})`}
              </Text>
            </Space>
          </div>
        </div>

        {/* Best For */}
        <div style={{ marginBottom: 16 }}>
          <Text
            strong
            style={{
              display: "block",
              marginBottom: 6,
              color: "#374151",
              fontSize: 12,
            }}
          >
            Best For
          </Text>
          <Space wrap size={4}>
            {template.bestFor.map((item) => (
              <Tag
                key={item}
                color="blue"
                style={{ borderRadius: 4, fontSize: 10 }}
              >
                {item}
              </Tag>
            ))}
          </Space>
        </div>

        {/* Tags */}
        <div>
          <Text
            strong
            style={{
              display: "block",
              marginBottom: 6,
              color: "#374151",
              fontSize: 12,
            }}
          >
            Tags
          </Text>
          <Space wrap size={4}>
            {template.tags.map((tag) => (
              <Tag key={tag} style={{ borderRadius: 4, fontSize: 10 }}>
                {tag}
              </Tag>
            ))}
          </Space>
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      bodyStyle={{ padding: 0, height: "90vh", overflow: "hidden" }}
      style={{ top: 20 }}
      destroyOnClose
    >
      <div style={{ display: "flex", height: "100%" }}>
        {/* Left Panel - Template List */}
        <div
          style={{
            width: "60%",
            borderRight: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div style={{ padding: 20, borderBottom: "1px solid #e5e7eb" }}>
            <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
              Choose a Pulse Report Template
            </Title>
            <Text style={{ color: "#6b7280" }}>
              {ALL_PULSE_REPORT_TEMPLATES.length} templates based on best practices from Stripe, HubSpot, Intercom & more
            </Text>
          </div>

          {/* Filters */}
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #f3f4f6" }}>
            {/* Category Pills */}
            <Space wrap size={8} style={{ marginBottom: 16 }}>
              <Button
                type={selectedCategory === "all" ? "primary" : "default"}
                size="small"
                onClick={() => setSelectedCategory("all")}
                icon={<AppstoreOutlined />}
                style={{ borderRadius: 6 }}
              >
                All ({ALL_PULSE_REPORT_TEMPLATES.length})
              </Button>
              {PULSE_TEMPLATE_CATEGORIES.map((category) => {
                const CategoryIcon = ICON_MAP[category.icon];
                return (
                  <Button
                    key={category.id}
                    type={selectedCategory === category.id ? "primary" : "default"}
                    size="small"
                    onClick={() => setSelectedCategory(category.id)}
                    icon={CategoryIcon ? <CategoryIcon /> : null}
                    style={{
                      borderRadius: 6,
                      borderColor:
                        selectedCategory === category.id
                          ? category.color
                          : undefined,
                      background:
                        selectedCategory === category.id
                          ? category.color
                          : undefined,
                    }}
                  >
                    {category.name} ({category.templates.length})
                  </Button>
                );
              })}
            </Space>

            {/* Search */}
            <Input
              placeholder="Search templates..."
              prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </div>

          {/* Template Grid */}
          <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
            {filteredTemplates.length > 0 ? (
              <Row gutter={[16, 16]}>
                {filteredTemplates.map((template) => (
                  <Col key={template.id} xs={24} sm={12}>
                    <TemplateCard template={template} />
                  </Col>
                ))}
              </Row>
            ) : (
              <Empty
                description="No templates found"
                style={{ marginTop: 60 }}
              />
            )}
          </div>
        </div>

        {/* Right Panel - Template Details */}
        <div
          style={{
            width: "40%",
            display: "flex",
            flexDirection: "column",
            background: "#fff",
          }}
        >
          <div style={{ flex: 1, overflow: "auto" }}>
            <TemplateDetailPanel template={selectedTemplate} />
          </div>

          {/* Action Footer */}
          <div
            style={{
              padding: 16,
              borderTop: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
            }}
          >
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              disabled={!selectedTemplate}
              onClick={handleUseTemplate}
              style={{
                background: selectedTemplate
                  ? REPORT_TYPE_COLORS[selectedTemplate.reportType]
                  : undefined,
                borderColor: selectedTemplate
                  ? REPORT_TYPE_COLORS[selectedTemplate.reportType]
                  : undefined,
              }}
            >
              Use This Template
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PulseTemplateGallery;
